import { useQuery } from '@apollo/client';
import { freeze } from '@reduxjs/toolkit';
import {
  AnalysisStatus,
  IPagination,
  ISearchAnalysisVisitsQuery,
  ISearchAnalysisVisitsQueryVariables,
  PermissionKey,
  QueryType,
} from '../apollo/graphqlSchema';
import { SEARCH_ANALYSIS_VISITS } from '../apollo/analysisVisit';
import { populateAnalysesVisits } from '../../redux/slices/analysesVisits';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useBootstrapSelector } from '../../redux/selectorHooks';
import { getUniqueAvailableSchemaIdsFromRolesByPermissionKey } from '../../pages/PermissionControls/utils';
import { assertNotNil } from '../types';

const pagination = freeze<IPagination>(
  {
    after: 0,
    first: 25,
  },
  true,
);

const statuses = freeze<AnalysisStatus[]>(
  [
    AnalysisStatus.Pending,
    AnalysisStatus.Running,
    AnalysisStatus.Success,
    AnalysisStatus.Failed,
    AnalysisStatus.Cancelled,
  ],
  true,
);

const useLoadAnalysesVisits = (
  queryTypes: QueryType[],
  permissionKeys: PermissionKey[],
) => {
  const dispatch = useAppDispatch();
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const { roles } = useAppSelector((state) => state.permission);
  const { allSchema } = permissionConfig;

  const allAvailableSchemaIds =
    getUniqueAvailableSchemaIdsFromRolesByPermissionKey(
      roles,
      permissionKeys,
      allSchema.id,
    );

  const handleQueryComplete = ({
    searchAnalysisVisits,
  }: ISearchAnalysisVisitsQuery) => {
    dispatch(populateAnalysesVisits(searchAnalysisVisits?.entities ?? []));
  };

  const { loading } = useQuery<
    ISearchAnalysisVisitsQuery,
    ISearchAnalysisVisitsQueryVariables
  >(SEARCH_ANALYSIS_VISITS, {
    onCompleted: handleQueryComplete,
    variables: {
      queryTypes,
      statuses,
      pagination,
      schemaIds: allAvailableSchemaIds,
    },
  });

  return loading;
};

export default useLoadAnalysesVisits;
